import type { PaletteId } from "./palettes";

export type EmissionPattern =
  | "plumes"
  | "full"
  | "center"
  | "ring"
  | "edges"
  | "line";
export type ExportScale = 1 | 2 | 3 | 4;

/**
 * All user-tweakable parameters. The seed is deliberately NOT a param —
 * it flows through EngineOptions.seed / Engine.randomize().
 *
 * Plot model: each particle is one STRAND that draws for its lifetime and
 * dies in place; ink is permanent; the piece settles when the last strand
 * finishes. Every param except `exposure` is reinit-class — changing it
 * re-plots the piece from step 0 (deterministic per seed).
 */
export interface Params {
  // Field
  flowScale: number;
  octaves: number;
  roughness: number;
  flowStrength: number;
  evolution: number;
  // Strands
  particleCount: number;
  lifetime: number;
  lifetimeJitter: number;
  stagger: number;
  emission: EmissionPattern;
  plumeCount: number;
  spread: number;
  halo: number;
  // Motion
  speed: number;
  windAngle: number;
  windStrength: number;
  vortex: number;
  attract: number;
  // Appearance
  dotSize: number;
  sizeJitter: number;
  dotOpacity: number;
  glow: number;
  /** Magnifies the view window about the center (1 = full window, capped).
   *  The sim domain is unchanged — a re-plot frames the piece closer. */
  zoom: number;
  exposure: number;
  palette: PaletteId;
  backgroundShade: number;
  symmetry: number;
}

export type ParamKey = keyof Params;

export type EngineErrorCode =
  | "webgl2-unsupported"
  | "float-buffer-unsupported"
  | "shader-compile-failed"
  | "export-too-large"
  | "context-lost";

export class EngineError extends Error {
  constructor(
    public readonly code: EngineErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "EngineError";
  }
}

export interface EngineEvents {
  /** Emitted ~once per second with a rolling average. */
  fps: { fps: number; frameMs: number };
  /** The last strand finished — the piece now holds as a permanent still. */
  settled: void;
  /** A new drawing started (reseed, replay, or any reinit-class change). */
  drawing: void;
  contextlost: void;
  contextrestored: void;
  error: EngineError;
}

export interface EngineOptions {
  params?: Partial<Params>;
  seed?: string;
  /** Debug: keep stepping while the document is hidden (default false). */
  ignoreHidden?: boolean;
}

/**
 * The contract between the engine and the UI. The UI must only ever hold
 * this type so a mock engine can stand in before the real one lands.
 */
export interface Engine {
  /** Live params apply next frame; reinit-class params rebuild particle state. */
  setParams(patch: Partial<Params>): void;
  getParams(): Readonly<Params>;
  /** Reseeds field + particles (params untouched). Returns the applied seed. */
  randomize(seed?: string): string;
  /** Re-plots the CURRENT seed from step 0 — an identical redraw. */
  replay(): void;
  /** True once the last strand finished and the piece holds. */
  readonly settled: boolean;
  getSeed(): string;
  pause(): void;
  resume(): void;
  readonly running: boolean;
  resize(cssWidth: number, cssHeight: number, dpr?: number): void;
  /** Largest export scale the GPU can honor for the current canvas size. */
  maxExportScale(): ExportScale;
  /** Deterministic offscreen re-render at scale; resolves to a PNG blob. */
  exportPNG(scale: ExportScale, onProgress?: (t: number) => void): Promise<Blob>;
  destroy(): void;
  on<K extends keyof EngineEvents>(
    ev: K,
    cb: (payload: EngineEvents[K]) => void,
  ): () => void;
}
